import { useCallback, useEffect, useState } from "react";

import { routes } from "@/constants";
import type { UserInterface } from "@/interfaces";
import {
    clearAuthHandler,
    getStoredUserHandler,
    isAuthenticatedHandler as checkAuth,
    userChangedEventName,
} from "@/utils";

import { useCurrentUser } from "./use-current-user";
import { useLogout } from "./use-logout";

export interface UseAuthReturnInterface {
    error: string | null,
    isAuthenticated: boolean,
    isLoading: boolean,
    onClearError: () => void,
    onFetchUser: () => Promise<void>,
    onLogout: () => Promise<void>,
    user: UserInterface | null,
}

export const useAuth = (): UseAuthReturnInterface => {
    const [user, setUser] = useState<UserInterface | null>(() => getStoredUserHandler());

    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => checkAuth());

    const [error, setError] = useState<string | null>(null);

    const {
        isLoading: isFetchingUser,
        onRun: fetchCurrentUser,
    } = useCurrentUser();

    const {
        isLoading: isLoggingOut,
        onRun: logoutAction,
    } = useLogout();

    const fetchUserHandler = useCallback(
        async () => {
            if (!checkAuth()) {
                setIsAuthenticated(false);

                return;
            }

            try {
                const fetchedUser = await fetchCurrentUser();

                setUser(fetchedUser);

                setIsAuthenticated(true);
            } catch {
                clearAuthHandler();

                setUser(null);

                setIsAuthenticated(false);
            }
        },
        [fetchCurrentUser],
    );

    const logoutHandler = useCallback(
        async () => {
            try {
                await logoutAction();
            } finally {
                setUser(null);

                setIsAuthenticated(false);

                window.location.href = routes.login;
            }
        },
        [logoutAction],
    );

    const clearErrorHandler = useCallback(
        () => setError(null),
        [],
    );

    useEffect(
        () => {
            const syncFromStorageHandler = () => {
                const storedUser = getStoredUserHandler();

                if (storedUser && checkAuth()) {
                    setUser(storedUser);

                    setIsAuthenticated(true);
                }
            };

            syncFromStorageHandler();

            window.addEventListener(
                userChangedEventName,
                syncFromStorageHandler,
            );

            return () => window.removeEventListener(
                userChangedEventName,
                syncFromStorageHandler,
            );
        },
        [],
    );

    useEffect(
        () => {
            if (error) clearErrorHandler();
        },
        [error, clearErrorHandler],
    );

    return {
        error,
        isAuthenticated,
        isLoading: isFetchingUser || isLoggingOut,
        onClearError: clearErrorHandler,
        onFetchUser: fetchUserHandler,
        onLogout: logoutHandler,
        user,
    };
};
